import {Auth0Connection} from '../types'
import auth0js, {CrossOriginLoginOptions, WebAuth} from 'auth0-js'

interface Auth0Config {
  domain: string
  clientID: string
  signupRedirectUri: string
  loginRedirectUri: string
}

type RedirectTarget = 'signupRedirectUri' | 'loginRedirectUri'

interface LoginRequest {
  realm: Auth0Connection.Authentication
  email: string
  password: string
  redirectTo: RedirectTarget
}

interface SignUpRequest {
  connection: Auth0Connection.Authentication
  email: string
  password: string
  family_name: string
  given_name: string
}

interface ChangePasswordRequest {
  connection: Auth0Connection.Authentication
  email: string
}

type ErrorSuccessCallback = (error: any, success?: any) => void

export interface AuthenticationClient {
  login: (request: LoginRequest, callback?: ErrorSuccessCallback) => void
  authorize: (connection: Auth0Connection | string) => void
  signup: (request: SignUpRequest, callback?: ErrorSuccessCallback) => void
  changePassword: (
    request: ChangePasswordRequest,
    callback?: ErrorSuccessCallback
  ) => void
}

export class Auth0Client implements AuthenticationClient {
  private client: WebAuth
  private config: Auth0Config

  constructor(config: Auth0Config = null) {
    this.config = config || this.getConfigFromWindow()

    this.client = new auth0js.WebAuth({
      domain: this.config.domain,
      clientID: this.config.clientID,
      redirectUri: this.config.signupRedirectUri,
      responseType: 'code',
    })
  }

  private getConfigFromWindow() {
    const {auth0: auth0Config} = (window as any).Config
    return auth0Config
  }

  authorize(connection) {
    this.client.authorize({
      redirectUri: this.config.loginRedirectUri,
      connection,
    })
  }

  login(request, callback) {
    const loginOptions = this.getLoginOptions(request)
    this.client.login(loginOptions, callback)
  }

  signup(request, callback) {
    this.client.signup(request, callback)
  }

  changePassword(request, callback) {
    this.client.changePassword(request, callback)
  }

  private getLoginOptions(request: LoginRequest): CrossOriginLoginOptions {
    return {
      realm: request.realm,
      email: request.email,
      password: request.password,
      redirectUri: this.getRedirectUri(request.redirectTo),
    }
  }

  private getRedirectUri(redirectTo: RedirectTarget) {
    switch (redirectTo) {
      case 'loginRedirectUri':
        return this.config.loginRedirectUri
      case 'signupRedirectUri':
        return this.config.signupRedirectUri
      default:
        return null
    }
  }
}
